'use client'

import { useState } from 'react'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { ConversationActionProps } from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'
import { TransactionSide } from '@marketplace-web/messaging/conversation-messaging-data'
import {
  updateDeliveryOptionChangeRequest,
  clickEvent,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { ResponseCode } from '@marketplace-web/core-api/api-client-util'
import { reloadPage } from '@marketplace-web/browser/browser-navigation-util'

import CancelChangeDeliveryOptionRequestModal from './CancelChangeDeliveryOptionRequestModal'
import GenericErrorModal from '../GenericErrorModal'
import { DeliveryOptionChangeRequestStatus } from '../../constants'

type Props = ConversationActionProps

const CancelChangeDeliveryOptionRequest = ({ primary, title, inverse, theme, size }: Props) => {
  const { track } = useTracking()

  const { conversation } = useConversationContext()

  const userSide = conversation?.transaction?.userSide
  const shipmentId = conversation?.transaction?.shipment?.id
  const transactionId = conversation?.transaction?.id

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false)

  const trackCancelClick = () => {
    if (!transactionId) return

    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: 'cancel_carrier_change_request',
        screen: 'message_reply',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const trackDeclineClick = () => {
    if (!transactionId) return

    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: 'decline_carrier_change_request',
        screen: 'message_reply',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const handleDecline = async () => {
    if (!shipmentId) return

    setIsLoading(true)

    const response = await updateDeliveryOptionChangeRequest({
      shipmentId,
      status: DeliveryOptionChangeRequestStatus.Cancelled,
    })

    if (response.code !== ResponseCode.Ok) {
      setIsLoading(false)
      setIsErrorModalVisible(true)
    }

    if (response.code === ResponseCode.Ok) {
      setIsLoading(false)
      setIsConfirmationModalOpen(false)
      reloadPage()
    }
  }

  const handleClick = () => {
    if (userSide === TransactionSide.Buyer) {
      trackDeclineClick()
      handleDecline()

      return
    }

    trackCancelClick()

    setIsConfirmationModalOpen(true)
  }

  const handleConfirmationModalClose = () => {
    setIsConfirmationModalOpen(false)
  }

  const handleErrorModalClose = () => {
    setIsErrorModalVisible(false)
  }

  return (
    <>
      <ConversationActionButton
        text={title}
        primary={primary}
        size={size}
        theme={theme}
        inverse={inverse}
        onClick={handleClick}
        testId="cancel-change-delivery-option-request-conversation-action"
        isLoading={isLoading}
      />
      <CancelChangeDeliveryOptionRequestModal
        shipmentId={shipmentId}
        isOpen={isConfirmationModalOpen}
        onClose={handleConfirmationModalClose}
      />
      <GenericErrorModal isShown={isErrorModalVisible} onClose={handleErrorModalClose} />
    </>
  )
}

export default CancelChangeDeliveryOptionRequest
